import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../translations/content';
import ProblemSection from './ProblemSection';
import USPSection from './USPSection';
import Pricing from './Pricing';
import Auth from './Auth';
import supabase from '../supabase/supabase';

const { FiArrowRight, FiTarget, FiTrendingUp, FiUsers, FiSearch } = FiIcons;

const Hero = () => {
  const { lang } = useLanguage();
  const navigate = useNavigate();
  const t = translations[lang].hero;
  const p = translations[lang].paths;
  const [session, setSession] = useState(null);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
    });

    return () => subscription.unsubscribe();
  }, []);

  const scrollToRegistration = () => {
    const element = document.getElementById('registration-section');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="flex flex-col">
      {/* 1. Hero Banner / Value Prop */}
      <section className="bg-gradient-to-br from-blue-50 via-white to-purple-50 py-20 min-h-[calc(100vh-64px)] flex items-center">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-4xl md:text-6xl font-black text-gray-900 mb-6 leading-tight tracking-tight uppercase"
            >
              {lang === 'en' ? (
                <>From <span className="text-blue-600">Idea</span> to <span className="text-purple-600">First Customer</span></>
              ) : (
                <>Von der <span className="text-blue-600">Idee</span> zum <span className="text-purple-600">ersten Kunden</span></>
              )}
            </motion.h1>
            <div className="mb-8 max-w-3xl mx-auto">
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="text-xl text-gray-600 mb-4 font-medium"
              >
                {t.subtitle}
              </motion.p>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="text-xl font-black text-blue-600 uppercase tracking-widest"
              >
                {t.subtitleHighlight}
              </motion.p>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="flex flex-col sm:flex-row gap-4 justify-center mb-16"
            >
              <button 
                onClick={scrollToRegistration}
                className="bg-blue-600 text-white px-8 py-4 rounded-xl hover:bg-blue-700 transition-all flex items-center justify-center space-x-2 shadow-lg shadow-blue-200 font-black uppercase tracking-widest transform hover:scale-105 active:scale-95"
              >
                <span>{t.ctaPrimary}</span>
                <SafeIcon icon={FiArrowRight} className="text-sm" />
              </button>

              <Link 
                to={p.steps}
                className="bg-white text-gray-900 border-2 border-gray-200 px-8 py-4 rounded-xl hover:border-blue-600 hover:text-blue-600 transition-all flex items-center justify-center space-x-2 font-black uppercase tracking-widest transform hover:scale-105 active:scale-95"
              >
                <span>{t.ctaSecondary}</span>
                <SafeIcon icon={FiSearch} className="text-sm" />
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto"
            >
              <div className="text-center p-6 bg-white/50 rounded-3xl backdrop-blur-sm border border-white shadow-sm">
                <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <SafeIcon icon={FiTarget} className="text-blue-600 text-2xl" />
                </div>
                <h3 className="text-lg font-black text-gray-900 mb-2 uppercase tracking-tight">{t.feature1}</h3>
                <p className="text-gray-600 text-sm font-medium">{t.feature1Desc}</p>
              </div>
              <div className="text-center p-6 bg-white/50 rounded-3xl backdrop-blur-sm border border-white shadow-sm">
                <div className="w-16 h-16 bg-purple-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <SafeIcon icon={FiTrendingUp} className="text-purple-600 text-2xl" />
                </div>
                <h3 className="text-lg font-black text-gray-900 mb-2 uppercase tracking-tight">{t.feature2}</h3>
                <p className="text-gray-600 text-sm font-medium">{t.feature2Desc}</p>
              </div>
              <div className="text-center p-6 bg-white/50 rounded-3xl backdrop-blur-sm border border-white shadow-sm">
                <div className="w-16 h-16 bg-green-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <SafeIcon icon={FiUsers} className="text-green-600 text-2xl" />
                </div>
                <h3 className="text-lg font-black text-gray-900 mb-2 uppercase tracking-tight">{t.feature3}</h3>
                <p className="text-gray-600 text-sm font-medium">{t.feature3Desc}</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 2. Problem Section */}
      <ProblemSection />

      {/* 3. USP Section */}
      <USPSection />

      {/* 4. Pricing Section */}
      <Pricing />

      {/* 5. Simplified Registration Section */}
      <section id="registration-section" className="py-24 bg-gray-50 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-6 tracking-tight leading-tight uppercase">
                {lang === 'de' ? 'Bereit für den ersten Schritt?' : 'Ready for the first step?'}
              </h2>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed font-medium">
                {lang === 'de'
                  ? 'Erhalte sofortigen Zugriff auf alle 10 Schritte und transformiere deine Idee in ein echtes Business.'
                  : 'Get instant access to all 10 steps and transform your idea into a real business.'
                }
              </p>
              
              <ul className="space-y-4">
                {[
                  lang === 'de' ? 'Kostenloser Account' : 'Free Account',
                  lang === 'de' ? 'Alle 10 Schritte freigeschaltet' : 'All 10 steps unlocked',
                  lang === 'de' ? 'Interaktive Strategie-Tools' : 'Interactive strategy tools'
                ].map((item, i) => (
                  <li key={i} className="flex items-center space-x-3 text-gray-700 font-bold">
                    <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center text-blue-600">
                      <SafeIcon icon={FiIcons.FiCheck} className="text-xs" />
                    </div>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              className="relative"
            >
              {/* Decorative background blur */}
              <div className="absolute -inset-4 bg-gradient-to-r from-blue-500 to-purple-500 rounded-[3rem] opacity-20 blur-2xl"></div>
              
              <div className="relative">
                {session ? (
                  <div className="bg-white p-12 rounded-[2.5rem] shadow-2xl border border-gray-100 text-center">
                    <div className="w-20 h-20 bg-green-50 rounded-2xl flex items-center justify-center mx-auto mb-6">
                      <SafeIcon icon={FiIcons.FiCheckCircle} className="text-4xl text-green-500" />
                    </div>
                    <h3 className="text-2xl font-black text-gray-900 mb-4 uppercase tracking-tight">
                      {lang === 'de' ? 'Willkommen zurück!' : 'Welcome Back!'}
                    </h3>
                    <p className="text-gray-600 mb-8 font-medium">
                      {lang === 'de' 
                        ? 'Du bist bereits angemeldet und hast vollen Zugriff.' 
                        : 'You are already signed in and have full access.'}
                    </p>
                    <button 
                      onClick={() => window.location.href = lang === 'de' ? '#/schritte' : '#/steps'}
                      className="w-full bg-blue-600 text-white font-black py-4 rounded-xl shadow-lg hover:bg-blue-700 transition-all uppercase tracking-widest"
                    >
                      {lang === 'de' ? 'Zu den Schritten' : 'Go to Steps'}
                    </button>
                  </div>
                ) : (
                  <Auth embedded={false} initialIsLogin={false} />
                )}
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Hero;